// ── Loot, equipment, extraction, and dungeon protocol ──
/** Loot item type identifiers.
 *
 * Phase V (2026-04-24) — v2 Softening pivot, rarity-tier deletion round:
 * IronSword, LeatherArmor, SwiftRing, VampireRing, CrystalOrb, WeaponShard
 * were removed per GDD §12 + §15.3. Round 13 Phase C (2026-04-27):
 * HealthPotion/GreaterHealthPotion removed and replaced with cooked food
 * item types (Bread, Stew variants, Tea, Feast, PetDish). */
export var ItemType;
(function (ItemType) {
    // ── Cooked food items (Round 13 Phase C) ──
    ItemType["FoodBread"] = "food_bread";
    ItemType["FoodStewVerdana"] = "food_stew_verdana";
    ItemType["FoodStewSablewind"] = "food_stew_sablewind";
    ItemType["FoodStewFrostmere"] = "food_stew_frostmere";
    ItemType["FoodStewEmberMire"] = "food_stew_ember_mire";
    ItemType["FoodTea"] = "food_tea";
    ItemType["FoodFeast"] = "food_feast";
    ItemType["FoodPetDish"] = "food_pet_dish";
    /** Fool vocation discovery food — a unique output from the 5th-craft mechanic.
     *  The specific discovery is identified by FoolDiscoveryMeta.discoveryId. */
    ItemType["FoodFoolDiscovery"] = "food_fool_discovery";
    // ── Equipment ──
    ItemType["SteelBlade"] = "steel_blade";
    ItemType["ShadowDagger"] = "shadow_dagger";
    ItemType["Excalibur"] = "excalibur";
    ItemType["ChainMail"] = "chain_mail";
    ItemType["PlateArmor"] = "plate_armor";
    ItemType["DragonscaleArmor"] = "dragonscale_armor";
    ItemType["AmuletOfVitality"] = "amulet_of_vitality";
    ItemType["CritRing"] = "crit_ring";
    ItemType["PhoenixPendant"] = "phoenix_pendant";
    ItemType["FlameSword"] = "flame_sword";
    ItemType["FrostAxe"] = "frost_axe";
    ItemType["VoidReaper"] = "void_reaper";
    ItemType["DragonScale"] = "dragon_scale";
    ItemType["PhoenixMantle"] = "phoenix_mantle";
    ItemType["CopperAmulet"] = "copper_amulet";
    ItemType["ShadowCloak"] = "shadow_cloak";
    ItemType["Raincoat"] = "raincoat";
    /**
     * Placeholder material introduced in Phase V (2026-04-24). A single drop
     * type exists so `LootSystem` has something to emit post-rarity-deletion;
     * the full materials system (wood, stone, thatch, ink, wax, cloth, ash,
     * ember, spiritsong, thawed-water) per GDD §12.1 is round 7+ scope.
     * Art: `icon-wood` sprite sheet (wave-1 art, 2026-04-24).
     */
    ItemType["MaterialWood"] = "material_wood";
    ItemType["MaterialStone"] = "material_stone";
    ItemType["MaterialThatch"] = "material_thatch";
    ItemType["MaterialCloth"] = "material_cloth";
    ItemType["MaterialInk"] = "material_ink";
    ItemType["MaterialWax"] = "material_wax";
    ItemType["MaterialAsh"] = "material_ash";
    /** Frostmere regional material. */
    ItemType["MaterialThawedWater"] = "material_thawed_water";
    /** Ember Mire regional material. */
    ItemType["MaterialEmber"] = "material_ember";
    /** Rare material — shrine ritual drops only. */
    ItemType["MaterialSpiritsong"] = "material_spiritsong";
    /** Ley-dust — auto-travel fuel (Round 9 Phase A). Drops near ley-pillars
     *  (15 %) and in restored areas (3 %). Consumed 1 per hop + attunement.
     *  Max stack 99, weightless per design spec §5.3. */
    ItemType["LeyDust"] = "ley_dust";
    /** Tree-seed — harvested from mature trees; used in Plant Sapling ritual
     *  (rituals.md §5) and Offer at Shrine ritual (rituals.md §4). */
    ItemType["MaterialSeed"] = "material_seed";
    /** Plain water — drawn from any well; accepted by Plant Sapling ritual
     *  (rituals.md §5) as a substitute for MaterialThawedWater. */
    ItemType["MaterialPlainWater"] = "material_plain_water";
})(ItemType || (ItemType = {}));
/** Item category for grouping behavior */
export var ItemCategory;
(function (ItemCategory) {
    ItemCategory["Consumable"] = "consumable";
    ItemCategory["Gear"] = "gear";
    ItemCategory["Equipment"] = "equipment";
    ItemCategory["Material"] = "material";
})(ItemCategory || (ItemCategory = {}));
/** Equipment slot identifiers */
export var EquipmentSlot;
(function (EquipmentSlot) {
    EquipmentSlot["Weapon"] = "weapon";
    EquipmentSlot["Armor"] = "armor";
    EquipmentSlot["Accessory"] = "accessory";
})(EquipmentSlot || (EquipmentSlot = {}));
/** Data-driven equipment stat lookup.
 *
 * Phase V (2026-04-24): stat rows for IronSword, LeatherArmor, SwiftRing,
 * VampireRing, CrystalOrb, and WeaponShard were deleted alongside their
 * ItemType enum entries. MaterialWood is intentionally absent — materials
 * are not equipment and carry no stat bonuses.
 */
export const EQUIPMENT_STATS = {
    // ── Weapons (ritual tools) ──
    [ItemType.SteelBlade]: { slot: EquipmentSlot.Weapon, bonusDefense: 1, bonusSpeed: 0, bonusMaxHP: 0 },
    [ItemType.ShadowDagger]: { slot: EquipmentSlot.Weapon, bonusDefense: 0, bonusSpeed: 10, bonusMaxHP: 0 },
    [ItemType.Excalibur]: { slot: EquipmentSlot.Weapon, bonusDefense: 0, bonusSpeed: 0, bonusMaxHP: 0 },
    [ItemType.FlameSword]: { slot: EquipmentSlot.Weapon, bonusDefense: 1, bonusSpeed: 0, bonusMaxHP: 0 },
    [ItemType.FrostAxe]: { slot: EquipmentSlot.Weapon, bonusDefense: 0, bonusSpeed: 0, bonusMaxHP: 0 },
    [ItemType.VoidReaper]: { slot: EquipmentSlot.Weapon, bonusDefense: 0, bonusSpeed: 10, bonusMaxHP: 0 },
    // ── Armor ──
    [ItemType.ChainMail]: { slot: EquipmentSlot.Armor, bonusDefense: 6, bonusSpeed: -10, bonusMaxHP: 10 },
    [ItemType.PlateArmor]: { slot: EquipmentSlot.Armor, bonusDefense: 10, bonusSpeed: -15, bonusMaxHP: 0 },
    [ItemType.DragonscaleArmor]: { slot: EquipmentSlot.Armor, bonusDefense: 12, bonusSpeed: 0, bonusMaxHP: 20 },
    [ItemType.DragonScale]: { slot: EquipmentSlot.Armor, bonusDefense: 9, bonusSpeed: 0, bonusMaxHP: 0 },
    [ItemType.PhoenixMantle]: { slot: EquipmentSlot.Armor, bonusDefense: 12, bonusSpeed: 15, bonusMaxHP: 0 },
    // ── Accessories ──
    [ItemType.AmuletOfVitality]: { slot: EquipmentSlot.Accessory, bonusDefense: 0, bonusSpeed: 0, bonusMaxHP: 15 },
    [ItemType.CritRing]: { slot: EquipmentSlot.Accessory, bonusDefense: 0, bonusSpeed: 0, bonusMaxHP: 0 },
    [ItemType.PhoenixPendant]: { slot: EquipmentSlot.Accessory, bonusDefense: 3, bonusSpeed: 0, bonusMaxHP: 25 },
    [ItemType.CopperAmulet]: { slot: EquipmentSlot.Accessory, bonusDefense: 1, bonusSpeed: 0, bonusMaxHP: 0 },
    [ItemType.ShadowCloak]: { slot: EquipmentSlot.Accessory, bonusDefense: 0, bonusSpeed: 20, bonusMaxHP: 0 },
    // ── Special dungeon rewards ──
    [ItemType.Raincoat]: { slot: EquipmentSlot.Armor, bonusDefense: 5, bonusSpeed: 10, bonusMaxHP: 10 },
};
/** Loot gameplay constants */
export const LOOT_DESPAWN_MS = 15000;
export const LOOT_DESPAWN_BLINK_MS = 3000;
/** Loot item rarity */
export var LootRarity;
(function (LootRarity) {
    LootRarity["Common"] = "common";
    LootRarity["Uncommon"] = "uncommon";
    LootRarity["Rare"] = "rare";
    LootRarity["Epic"] = "epic";
    LootRarity["Legendary"] = "legendary";
})(LootRarity || (LootRarity = {}));
/** Loot pickup proximity range in pixels */
export const LOOT_PICKUP_RANGE = 32;
// EXTRACTION_ZONE_RANGE + EXTRACTION_DURATION_MS removed in v2 pivot.
// Loot message type constants
export const MSG_LOOT_SPAWN = 'loot_spawn';
export const MSG_LOOT_DESPAWN = 'loot_despawn';
export const MSG_LOOT_PICKUP = 'loot_pickup';
// Extraction MSG_* constants (MSG_EXTRACTION_{ZONE,START,CANCEL,COMPLETE,
// REQUEST}) removed in v2 Softening pivot.
/** Client → Server: request to pick up nearby loot */
export const MSG_LOOT_PICKUP_REQUEST = 'loot_pickup_request';
/** Client → Server: equip an item from inventory */
export const MSG_EQUIP_ITEM = 'equip_item';
/** Client → Server: unequip an item from a slot */
export const MSG_UNEQUIP_ITEM = 'unequip_item';
/** Server → Client: inventory state update (full sync) */
export const MSG_INVENTORY_SYNC = 'inventory_sync';
// ── Dungeon themes ──
/** Visual theme applied to a dungeon floor */
export var DungeonTheme;
(function (DungeonTheme) {
    DungeonTheme["Overworld"] = "overworld";
    DungeonTheme["Dungeon"] = "dungeon";
    DungeonTheme["Cave"] = "cave";
    DungeonTheme["Crypt"] = "crypt";
    DungeonTheme["Lava"] = "lava";
    DungeonTheme["StiltVillage"] = "stilt_village";
})(DungeonTheme || (DungeonTheme = {}));
/** Default dungeon progression config — loaded if no config file overrides */
export const DEFAULT_DUNGEON_PROGRESSION = {
    totalFloors: 5,
    enemyScalingPerFloor: 0.15,
    extraRoomsPerFloor: 1,
    bossFloor: 5,
    floorTransitionMs: 3000,
    playerScalingPerExtra: 0.35,
    extraEnemiesPerFloor: 1,
    extraEnemiesPerPlayer: 1,
    bossHPMultiplier: 3,
    bossExtraDamagePerPlayer: 0,
    floorRarityBonus: 0.05,
    minCommonDropChance: 0.05,
    bossGuaranteedMinRarity: LootRarity.Rare,
};
// ── Floor-transition + boss-phase + boss-summon MSG constants removed
//    2026-04-24 post-V1-purge: v2 Softening has no procedural floors and
//    no v1-style boss encounters. Guardian encounters (GDD §9.6) will
//    define fresh message shapes when they arrive.
// ── Game flow — room phase removed in v2 Softening pivot (V-3, 2026-04-24) ──
//
// The `RoomPhase` enum, `RoomPhaseChangeEvent`, and `MSG_ROOM_PHASE` lived on
// the deleted ExpeditionRoom; OverworldRoom never emitted them. `MSG_READY` /
// `MSG_PLAYER_READY` / `PlayerReadyEvent` belonged to the lobby-phase flow
// which is also gone. Keeping the section heading below as an anchor for the
// future v2 expedition lifecycle events (ritual start/cancel, etc.) — see
// GDD §9.7 on expeditions as a scaffolded concept.
// ── Room management & difficulty ──
/** Difficulty levels for dungeon runs */
export var Difficulty;
(function (Difficulty) {
    Difficulty["Normal"] = "normal";
    Difficulty["Hard"] = "hard";
    Difficulty["Nightmare"] = "nightmare";
})(Difficulty || (Difficulty = {}));
/** Difficulty display labels */
export const DIFFICULTY_LABELS = {
    [Difficulty.Normal]: 'Normal',
    [Difficulty.Hard]: 'Hard',
    [Difficulty.Nightmare]: 'Nightmare',
};
/** Difficulty stat scaling multipliers (applied on top of floor/player scaling) */
export const DIFFICULTY_SCALING = {
    [Difficulty.Normal]: { statMultiplier: 1.0, lootRarityBonus: 0, floorCount: 5 },
    [Difficulty.Hard]: { statMultiplier: 1.5, lootRarityBonus: 0.05, floorCount: 7 },
    [Difficulty.Nightmare]: { statMultiplier: 2.0, lootRarityBonus: 0.10, floorCount: 10 },
};
/** Client → Server: request to list available rooms */
export const MSG_ROOM_LIST_REQUEST = 'room_list_request';
/** Server → Client: available rooms response */
export const MSG_ROOM_LIST = 'room_list';
// ── Overworld messages ──
/** Server → Client: player entered a dungeon gate zone */
export const MSG_DUNGEON_GATE_ENTER = 'dungeon_gate_enter';
/**
 * Client → Server: player requests to leave the dungeon and return to the
 * overworld. Introduced in V-3 (2026-04-24) as part of the in-world zone
 * switch — replaced the v1 MSG_ROOM_TRANSFER pathway where dungeon entry
 * moved the player between Colyseus rooms. No payload: the server infers
 * which dungeon to leave from the player's current `zone` schema field.
 */
export const MSG_DUNGEON_LEAVE = 'dungeon_leave';
// ── Home zone messages (Phase N-revised) ──
/** Server → Client: player entered a home zone gate */
export const MSG_HOME_ZONE_ENTER = 'home_zone_enter';
/**
 * Client → Server: player requests to leave the home zone and return to
 * the overworld. Mirrors `MSG_DUNGEON_LEAVE` — the server infers which
 * home to leave from the player's current `zone` schema field.
 */
export const MSG_HOME_ZONE_LEAVE = 'home_zone_leave';
// ── Town zone messages (AGE-1284) ──
/** Server → Client: player entered a town zone */
export const MSG_TOWN_ZONE_ENTER = 'town_zone_enter';
/** Client → Server: player requests to leave the town zone */
export const MSG_TOWN_ZONE_LEAVE = 'town_zone_leave';
/** Client → Server: player requests to change town screen */
export const MSG_TOWN_SCREEN_CHANGE = 'town_screen_change';
/** Server → Client: town screen changed (broadcast to co-present players) */
export const MSG_TOWN_SCREEN_CHANGED = 'town_screen_changed';
/** Server → Client: NPC spawned in the overworld */
export const MSG_NPC_SPAWN = 'npc_spawn';
/** Client → Server: player interacted with an NPC */
export const MSG_NPC_INTERACT = 'npc_interact';
/** Server → Client: NPC dialogue payload */
export const MSG_NPC_DIALOGUE = 'npc_dialogue';
/**
 * Server → Client: authoritative player position for reconciliation.
 *
 * OverworldRoom does not deliver MapSchema player entries through the
 * colyseus.js schema callback path, so `player.onChange` never fires and
 * the client cannot use the schema-based reconciliation pattern that
 * GameScene uses. This message is the custom workaround — the server
 * broadcasts each client's authoritative (x, y) at a low rate (e.g. 4 Hz)
 * and the client snaps `localCartX/Y` when its predicted position drifts
 * beyond threshold.
 */
export const MSG_PLAYER_POSITION = 'player_position';
/**
 * Server → Client: compact periodic tick with every live entity's current
 * position. ExpeditionRoom's Colyseus schema sync is not delivering state.players
 * or state.enemies to clients (even top-level scalars like roomPhase come
 * through as undefined), so the client can't rely on schema onChange for
 * movement. This message replaces that pipe — server broadcasts ~10 Hz,
 * client reconciles local player and updates interp targets for remote
 * players and enemies.
 *
 * Batched rather than per-entity so 30 enemies + 4 players = 1 message,
 * not 34.
 */
export const MSG_ENTITIES_TICK = 'entities_tick';
// `MSG_ROOM_TRANSFER` / `RoomTransferEvent` / `RoomTransferOptions` removed
// in V-3 (2026-04-24). See `MSG_DUNGEON_LEAVE` above and GDD §9.7 — dungeon
// entry is now an in-world zone switch, not a Colyseus room transfer.
/** NPC interaction proximity range in pixels */
export const NPC_INTERACT_RANGE = 48;
// ── Tide system (Stilt Village dungeon) ──
/** Tide cycle duration in milliseconds (90 seconds) */
export const TIDE_CYCLE_MS = 90_000;
/** Number of discrete tide levels (0 = low tide, max = high tide) */
export const TIDE_LEVELS = 3;
/** Server → Client: tide level changed */
export const MSG_TIDE_UPDATE = 'tide_update';
/** Server → Client: bell landmark rings at tide peak */
export const MSG_BELL_RING = 'bell_ring';
// ── Tutorial first-day messages (Phase PP) ──
/** Server → Client: tutorial beat advanced — carries dialogue + state. */
export const MSG_TUTORIAL_BEAT = 'tutorial_beat';
/** Server → Client: Hearthkeeper NPC spawn (tutorial-only, home zone). */
export const MSG_TUTORIAL_NPC_SPAWN = 'tutorial_npc_spawn';
/** Server → Client: Hearthkeeper NPC despawn (tutorial complete or Beat 4+). */
export const MSG_TUTORIAL_NPC_DESPAWN = 'tutorial_npc_despawn';
/** Client → Server: player interacts with the tutorial sapling cluster. */
export const MSG_SAPLING_INTERACT = 'sapling_interact';
// ── Treasure creek interaction (AGE-1023) ──
/** Client → Server: player interacts with an odd stone in a treasure creek. */
export const MSG_CREEK_STONE_INTERACT = 'creek_stone_interact';
/** Interaction range for creek stones (px) — slightly larger than NPC range. */
export const CREEK_STONE_INTERACT_RANGE = 56;
/** Seasonal cooldown duration for creek loot (ms) — 7 real-world days. */
export const CREEK_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
// ── Debug / perf messages ──
/** Client → Server: RTT probe — echo payload back immediately */
export const MSG_PING = '__ping__';
/** Server → Client: RTT probe echo response */
export const MSG_PONG = '__pong__';
//# sourceMappingURL=loot.js.map